/**
 * Tokenvoorziening voor de API-client (ADR-003).
 *
 * De v0.7-stub stuurde vaste `X-Tenant-Id`- en `X-Roles`-headers mee. Dat was
 * niet alleen dood gewicht — de backend weigert die headers sinds 2026-08-05
 * buiten `ENVIRONMENT=development` — maar ook een verkeerd model: de tenant
 * hoort in een gevalideerd token te zitten, niet in een header die de client
 * zelf invult. `backend/app/core/security.py` is daar expliciet over: row-level
 * security beschermt niets als de aanroeper zelf mag bepalen wie hij is.
 *
 * Deze module levert daarom één interface met drie implementaties:
 *
 *   `anonymous`   standaard. Geen token, dus 401 van de backend. Een build die
 *                 vergeet iets in te stellen krijgt géén toegang in plaats van
 *                 volledige toegang — dezelfde fail-closed keuze als de backend.
 *   `local`       ontwikkeling. Bearer-token uit de omgeving of uit
 *                 sessionStorage, plus de opt-in dev-headeradapter waarmee de
 *                 backend lokaal draait. Werkt uitsluitend in een dev-build.
 *   `entra`       productie. MSAL-browser: autorisatiecode met PKCE via een
 *                 redirect, tokens in `sessionStorage`.
 *
 * De keuze valt op `VITE_AUTH_MODE`. Componenten kennen alleen deze interface
 * en de context eromheen (`auth/AuthContext.tsx`); geen enkel scherm praat
 * rechtstreeks met MSAL.
 */

export type AuthMode = "anonymous" | "local" | "entra";

/** De ingelogde gebruiker zoals de schil hem toont. */
export type AtlasAccount = {
  /** `sub`-claim, of de lokale ontwikkelidentiteit. */
  subject: string;
  displayName: string;
  email?: string;
  /** Platformtenant (claim `atlas_tenant_id`, niet Entra's `tid` — ADR-003). */
  tenantId?: string;
  /** App-rollen: `viewer`, `editor`, `admin`. */
  roles: string[];
  /**
   * Waar de rollen vandaan komen, zodra het platform is gevraagd (`GET /me`):
   * `platform` als een beheerder de rol heeft gezet, anders `entra`.
   */
  roleSource?: "platform" | "entra";
  /**
   * Sturingsrollen (K10), uit `GET /me`: welke dashboardlagen deze gebruiker
   * ziet (`strategic`, `tactical`, `operational`). `undefined` zolang het
   * platform nog niet is gevraagd; een lege lijst is "geen dashboard".
   */
  steeringLayers?: string[];
};

export interface TokenProvider {
  readonly mode: AuthMode;
  /** Menselijke naam van de modus, voor het gebruikersmenu en de foutmelding. */
  readonly label: string;
  /**
   * Rondt het opstarten af en levert het account op dat daaruit volgt.
   *
   * Bestaat omdat MSAL de terugkeer van de inlogpagina **asynchroon**
   * afhandelt: de autorisatiecode staat in de URL en `handleRedirectPromise()`
   * moet die eerst inwisselen. `getAccount()` is synchroon — de schil rendert
   * ermee — en zou vóór dat moment `null` teruggeven, waarna een ingelogde
   * gebruiker alsnog het inlogscherm ziet. Wie het account wil weten, wacht
   * dus eerst hierop; daarna blijft `getAccount()` het antwoord geven.
   *
   * Mag meermaals worden aangeroepen; het onderliggende werk gebeurt één keer.
   */
  initialize(): Promise<AtlasAccount | null>;
  /** Bearer-token voor de volgende aanroep, of `null` als er geen is. */
  getAccessToken(): Promise<string | null>;
  /** Wie er is ingelogd, voor zover bekend zonder netwerkaanroep. */
  getAccount(): AtlasAccount | null;
  signIn(): Promise<void>;
  signOut(): Promise<void>;
  /**
   * Alleen de ontwikkeladapter uit ADR-003 vult dit: de headers waarmee de
   * backend in `AUTH_MODE=dev_header` draait. In elke andere modus leeg.
   */
  getDevHeaders?(): Record<string, string>;
}

/** Sleutel waaronder een handmatig geplakt ontwikkeltoken wordt bewaard. */
const DEV_TOKEN_STORAGE_KEY = "atlas.dev.accessToken";

/* ------------------------------------------------------------------ anoniem */

/**
 * Geen credentials. Elke beveiligde aanroep levert 401 op, en dat is de
 * bedoeling: liever zichtbaar geen toegang dan onzichtbaar te veel.
 */
export const createAnonymousTokenProvider = (): TokenProvider => ({
  mode: "anonymous",
  label: "Niet ingelogd",
  initialize: async () => null,
  getAccessToken: async () => null,
  getAccount: () => null,
  signIn: async () => {
    throw new Error(
      "Inloggen is nog niet ingericht. Zet VITE_AUTH_MODE=local voor lokale " +
        "ontwikkeling, of rond de Entra ID-registratie af (ADR-003).",
    );
  },
  signOut: async () => {},
});

/* ------------------------------------------------------------------- lokaal */

type LocalOptions = {
  accessToken?: string;
  tenantId?: string;
  subject?: string;
  displayName?: string;
  email?: string;
  roles?: string[];
};

/**
 * Ontwikkelimplementatie. Twee paden, beide expliciet aan te zetten en geen
 * van beide met een ingebakken waarde:
 *
 *  1. `VITE_DEV_ACCESS_TOKEN`, of een token dat je in de console in
 *     sessionStorage zet. Dat gaat als `Authorization: Bearer` mee en werkt
 *     tegen een backend in `AUTH_MODE=oidc`.
 *  2. `VITE_DEV_TENANT_ID` (+ optioneel gebruiker en rollen). Dat levert de
 *     `X-`-headers van de ontwikkeladapter, waarmee een backend in
 *     `AUTH_MODE=dev_header` lokaal bereikbaar is.
 *
 * Beide zijn onbereikbaar in een productiebuild: `assertDevOnly` gooit dan.
 */
export const createLocalTokenProvider = (options: LocalOptions = {}): TokenProvider => {
  assertDevOnly();

  const roles = options.roles ?? [];
  // Wie een token of een tenant heeft ingesteld, hoort de applicatie te zien:
  // de inlogbewaking (`RequireAuth`) laat alleen door wie een account heeft.
  // Zonder deze terugval zou `VITE_DEV_ACCESS_TOKEN` zonder `VITE_DEV_USER`
  // wel werkende API-aanroepen opleveren maar toch op het inlogscherm blijven
  // hangen. Is er niets ingesteld, dan blijft het account bewust `null`.
  const identified = Boolean(options.subject || options.accessToken || options.tenantId);
  const account: AtlasAccount | null = identified
    ? {
        subject: options.subject ?? "lokale-ontwikkelaar",
        displayName: options.displayName ?? options.subject ?? "Lokale ontwikkelaar",
        email: options.email,
        tenantId: options.tenantId,
        roles,
      }
    : null;

  return {
    mode: "local",
    label: "Lokale ontwikkeling",
    initialize: async () => account,
    getAccessToken: async () => options.accessToken ?? readStoredDevToken(),
    getAccount: () => account,
    signIn: async () => {
      throw new Error(
        "In de lokale modus wordt niet ingelogd. Zet VITE_DEV_ACCESS_TOKEN of " +
          "VITE_DEV_TENANT_ID in je .env.local.",
      );
    },
    signOut: async () => clearStoredDevToken(),
    getDevHeaders: () => {
      if (!options.tenantId) return {};
      // Exact de drie headers die backend/app/core/security.py in
      // AUTH_MODE=dev_header leest. Geen standaardwaarden: ontbreekt de
      // tenant, dan gaat er niets mee en antwoordt de backend 401.
      const headers: Record<string, string> = { "X-Tenant-Id": options.tenantId };
      if (options.subject) headers["X-User"] = options.subject;
      if (roles.length > 0) headers["X-Roles"] = roles.join(",");
      return headers;
    },
  };
};

const readStoredDevToken = (): string | null => {
  try {
    return globalThis.sessionStorage?.getItem(DEV_TOKEN_STORAGE_KEY) ?? null;
  } catch {
    // Privémodus of geblokkeerde opslag: dan is er simpelweg geen token.
    return null;
  }
};

const clearStoredDevToken = (): void => {
  try {
    globalThis.sessionStorage?.removeItem(DEV_TOKEN_STORAGE_KEY);
  } catch {
    /* geen opslag beschikbaar — niets te wissen */
  }
};

function assertDevOnly(): void {
  if (import.meta.env.PROD) {
    throw new Error(
      "De lokale tokenprovider is alleen toegestaan in een ontwikkelbuild. " +
        "Zet VITE_AUTH_MODE=entra voor een gepubliceerde omgeving (ADR-003).",
    );
  }
}

/* -------------------------------------------------------------- Entra ID ---
 *
 * De productie-implementatie (ADR-003). De app-registraties worden gemaakt door
 * `infra/azure/08-entra-app.sh`; dat script drukt exact de vier waarden af die
 * hieronder worden gelezen.
 *
 * Drie keuzes die het gedrag bepalen:
 *
 *  - **Autorisatiecode met PKCE, via een redirect.** Geen impliciete flow (die
 *    zet tokens in de URL) en geen popup (die wordt geblokkeerd zodra iemand
 *    het scherm in een tweede tabblad opent).
 *  - **`sessionStorage`, niet `localStorage`.** Een token dat een gesloten
 *    tabblad overleeft, overleeft ook een gedeelde werkplek.
 *  - **Fail-closed bij ontbrekende configuratie.** Een build zonder client-id
 *    krijgt geen anonieme toegang maar een leesbare fout, hetzelfde principe
 *    als `verify_auth_configuration()` aan de backendkant.
 *
 * De platformtenant zit in het **access**-token (de directory-extensie uit
 * stage 08), niet in het id-token. Wat de schil hier toont komt daarom uit het
 * id-token en is bedoeld voor het gebruikersmenu; de autorisatie gebeurt bij de
 * backend, op het access-token.
 */
type EntraOptions = {
  clientId?: string;
  tenantId?: string;
  authority?: string;
  redirectUri?: string;
  apiScope?: string;
};

type MsalClaims = {
  sub?: string;
  oid?: string;
  name?: string;
  preferred_username?: string;
  roles?: string[];
  [claim: string]: unknown;
};

export const createEntraTokenProvider = (options: EntraOptions = {}): TokenProvider => {
  const clientId = options.clientId ?? env.VITE_ENTRA_CLIENT_ID;
  const tenantId = options.tenantId ?? env.VITE_ENTRA_TENANT_ID;
  const apiScope = options.apiScope ?? env.VITE_ENTRA_API_SCOPE;
  const authority =
    options.authority ?? env.VITE_ENTRA_AUTHORITY ?? `https://login.microsoftonline.com/${tenantId}`;
  const redirectUri = options.redirectUri ?? env.VITE_ENTRA_REDIRECT_URI ?? window.location.origin;

  const missing = [
    ["VITE_ENTRA_CLIENT_ID", clientId],
    ["VITE_ENTRA_TENANT_ID", tenantId],
    ["VITE_ENTRA_API_SCOPE", apiScope],
  ]
    .filter(([, value]) => !value)
    .map(([name]) => name);
  if (missing.length > 0) {
    throw new Error(
      `Entra ID is gekozen (VITE_AUTH_MODE=entra) maar ${missing.join(", ")} ontbreekt. ` +
        "Draai infra/azure/08-entra-app.sh; dat script drukt de waarden af.",
    );
  }

  const scopes = [apiScope as string];
  let cachedAccount: AtlasAccount | null = null;
  // MSAL v3+ moet worden geïnitialiseerd vóór het eerste gebruik, en
  // `handleRedirectPromise` moet ná terugkeer van de inlogpagina draaien —
  // anders blijft de code in de URL staan en komt er nooit een account.
  const clientPromise = (async () => {
    const { PublicClientApplication } = await import("@azure/msal-browser");
    const client = new PublicClientApplication({
      auth: { clientId: clientId as string, authority, redirectUri },
      cache: { cacheLocation: "sessionStorage" },
    });
    await client.initialize();
    const result = await client.handleRedirectPromise();
    if (result?.account) client.setActiveAccount(result.account);
    if (!client.getActiveAccount()) {
      const [first] = client.getAllAccounts();
      if (first) client.setActiveAccount(first);
    }
    // `getAccount()` is synchroon (de schil rendert ermee), dus het account
    // wordt hier één keer omgezet en bewaard.
    cachedAccount = toAtlasAccount(client.getActiveAccount());
    return client;
  })();

  return {
    mode: "entra",
    label: "Microsoft Entra ID",
    initialize: async () => {
      await clientPromise;
      return cachedAccount;
    },
    getAccessToken: async () => {
      const client = await clientPromise;
      const account = client.getActiveAccount();
      if (!account) return null;
      try {
        const result = await client.acquireTokenSilent({ scopes, account });
        return result.accessToken;
      } catch {
        // Stil vernieuwen lukte niet (verlopen sessie, nieuw toestemmingsverzoek,
        // MFA). Doorsturen naar de inlogpagina is hier de enige uitweg; de
        // aanroep die het token vroeg wordt niet meer beantwoord.
        await client.acquireTokenRedirect({ scopes, account });
        return null;
      }
    },
    getAccount: () => cachedAccount,
    signIn: async () => {
      const client = await clientPromise;
      await client.loginRedirect({ scopes });
    },
    signOut: async () => {
      const client = await clientPromise;
      cachedAccount = null;
      await client.logoutRedirect();
    },
  };
};

/**
 * Een MSAL-account naar het accountmodel van de schil.
 *
 * De rollen komen uit de `roles`-claim van het **id**-token, en zijn hier
 * alleen voor wat het scherm toont (welke knoppen zichtbaar zijn). De echte
 * autorisatie gebeurt bij de backend op het access-token — een frontend die
 * zijn eigen rollen gelooft, beschermt niets.
 */
function toAtlasAccount(
  account: { idTokenClaims?: object; name?: string; username?: string; homeAccountId?: string } | null,
): AtlasAccount | null {
  if (!account) return null;
  const claims = (account.idTokenClaims ?? {}) as MsalClaims;
  return {
    subject: String(claims.oid ?? claims.sub ?? account.homeAccountId ?? ""),
    displayName: account.name ?? String(claims.preferred_username ?? account.username ?? ""),
    email: account.username,
    roles: Array.isArray(claims.roles) ? claims.roles.map(String) : [],
  };
}

/* ------------------------------------------------------------------- keuze */

const env = import.meta.env;

/** Leest de gekozen modus uit de omgeving; onbekend of leeg wordt `anonymous`. */
export const configuredAuthMode = (): AuthMode => {
  const raw = (env.VITE_AUTH_MODE ?? "").trim();
  if (raw === "local" || raw === "entra" || raw === "anonymous") return raw;
  return "anonymous";
};

/** Bouwt de provider die bij de omgeving hoort. */
export const createTokenProvider = (mode: AuthMode = configuredAuthMode()): TokenProvider => {
  switch (mode) {
    case "local":
      return createLocalTokenProvider({
        accessToken: env.VITE_DEV_ACCESS_TOKEN,
        tenantId: env.VITE_DEV_TENANT_ID,
        subject: env.VITE_DEV_USER,
        displayName: env.VITE_DEV_USER_NAME ?? env.VITE_DEV_USER,
        email: env.VITE_DEV_USER,
        roles: (env.VITE_DEV_ROLES ?? "")
          .split(",")
          .map((role) => role.trim())
          .filter(Boolean),
      });
    case "entra":
      return createEntraTokenProvider();
    case "anonymous":
    default:
      return createAnonymousTokenProvider();
  }
};
